import "@fortawesome/fontawesome-free/css/fontawesome.min.css";
import { useKeycloak } from "@react-keycloak/web";
import "font-awesome/css/font-awesome.min.css";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { translate } from "react-switch-lang";
import { Button, Card, CardHeader, Col, Container, Row, Table } from "reactstrap";
import { DisplayMode } from "../../../../models/displayModeDto";
import { SortDirection } from "../../../../models/sortDirection";
import { TrainingPathDto } from "../../../../models/trainingPathDto";
import hasRoles from "../../../../security/hasRoles";
import { useHttpClient } from "../../../../security/hooks/axiosProvider";
import Breadcrumb from "../../../../shared/breadcrumb/breadCrumb";
import { RootState } from "../../../../store";
import PaginationComponent from "../components/pagination";
import SpinnerComponent from "../components/spinnerComponent";
import TrainingPath from "../trainingPath";
import "./traingPathList.scss";
import { RequestType } from "./traingPathListDisplayType";
import styles from "./training-path-list.module.scss";

const TraingPathList: React.FC<{ requestType: RequestType; t: (key: string) => string; tabId: string }> = props => {
  const { keycloak } = useKeycloak();
  const history = useHistory();
  const httpClient = useHttpClient(keycloak.authenticated);
  const hasRoleAdmin = hasRoles(["ROLE_ELEARNING_ADMIN"], keycloak);
  const hasRoleUser = hasRoles(["ROLE_ELEARNING_USER"], keycloak);
  const [displayMode, setDisplayMode] = useState(hasRoleUser || !keycloak.authenticated ? DisplayMode.GALLERY : DisplayMode.LIST);
  const [traingPathList, setTrainingPathList] = useState<TrainingPathDto[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [trainingPathsPerPage] = useState(8);
  const [pageNumbers, setPageNumbers] = useState(0);
  const [done, setDone] = useState(false);
  const [offline, setOffline] = useState(false);
  const currentLang = useSelector((state: RootState) => state.language.currentLang);
  const [sortDirection, setSortDirection] = useState(SortDirection.SORT);
  const [sortIcon, setSortIcon] = useState("sort");
  const [sortColumn, setSortColumn] = useState("");
  const [isArchivedList, setIsArchivedList] = useState(false);

  useEffect(() => {
    getCuurentTrainingPathSortedList(currentPage);
    // eslint-disable-next-line
  }, [currentLang]);

  const fetchTrainingPath = () => {
    if (!done) {
      getCuurentTrainingPathSortedList(currentPage);
    }
  };

  useEffect(() => {
    fetchTrainingPath();
  });

  const refreshTrainaingPathList = () => {
    getCuurentTrainingPathSortedList(currentPage);
  };

  function getRequestType() {
    switch (props.requestType) {
      case RequestType.ADMIN_VISITOR:
        return "perPage";
      case RequestType.USERS_TRAINING:
        return "byUser";
      case RequestType.USER_OTHER_TRAINING:
        return "notUsers";
      case RequestType.ARCHIVE: {
        setIsArchivedList(true);
        return "perPage";
      }
      default:
        return "perPage";
    }
  }

  const sortTrainingPathList = (sortColumnName: string) => {
    setSortColumn(sortColumnName);
    let asc = false;
    if (sortDirection === SortDirection.SORT || sortDirection === SortDirection.SORT_DESC || sortColumnName !== sortColumn) {
      asc = true;
      setSortIcon("sort-asc");
    } else {
      asc = false;
      setSortIcon("sort-desc");
    }
    httpClient.current
      ?.get(`/trainingPath/${getRequestType()}/byOrder`, {
        params: { page: currentPage, size: trainingPathsPerPage, language: currentLang.toUpperCase(), sortColumn: sortColumnName, asc: asc, archived: isArchivedList },
      })
      .then(response => {
        setTrainingPathList(response?.data?.content as TrainingPathDto[]);
        setPageNumbers(response?.data?.totalPages);
        asc ? setSortDirection(SortDirection.SORT_ASC) : setSortDirection(SortDirection.SORT_DESC);
        setDone(true);
      });
  };

  const getTrainingPathListNotSorted = (index: number) => {
    httpClient.current
      ?.get(`/trainingPath/${getRequestType()}`, { params: { page: index, size: trainingPathsPerPage, language: currentLang.toUpperCase(), archived: isArchivedList } })
      .then(response => {
        setTrainingPathList(response?.data?.content as TrainingPathDto[]);
        setPageNumbers(response?.data?.totalPages);
        setDone(true);
      });
  };

  const getTrainingPathListSorted = (index: number, asc: boolean) => {
    httpClient.current
      ?.get(`/trainingPath/${getRequestType()}/byOrder`, {
        params: { page: index, size: trainingPathsPerPage, language: currentLang.toUpperCase(), sortColumn: sortColumn, asc: asc, archived: isArchivedList },
      })
      .then(response => {
        setTrainingPathList(response?.data?.content as TrainingPathDto[]);
        setPageNumbers(response?.data?.totalPages);
        setDone(true);
      });
  };

  const getCuurentTrainingPathSortedList = (index: number) => {
    switch (sortDirection) {
      case SortDirection.SORT:
        getTrainingPathListNotSorted(index);
        break;
      case SortDirection.SORT_ASC:
        getTrainingPathListSorted(index, true);
        break;
      case SortDirection.SORT_DESC:
        getTrainingPathListSorted(index, false);
        break;
    }
  };

  const paginate = (index: number, e: any) => {
    e.preventDefault();
    setCurrentPage(index);
    getCuurentTrainingPathSortedList(index);
  };

  const deleteTrainingPath = (trainingPathID: number) => {
    if (trainingPathID) {
      httpClient.current?.delete(`/trainingPath/${trainingPathID}`).then(() => {
        getCuurentTrainingPathSortedList(currentPage);
      });
    }
  };

  const activateTrainingPath = (trainingPath: TrainingPathDto) => {
    trainingPath.status = !trainingPath.status;
    httpClient.current?.put(`/trainingPath`, trainingPath).then(() => {
      getCuurentTrainingPathSortedList(currentPage);
      if(trainingPath.status){
        setOffline(true);
        httpClient.current?.get(`/trainingPath/offline/${trainingPath.id}`).then(() => {
        setOffline(false);
      });
      }
    });
    
  };

  const archiveTrainingPath = (trainingPath: TrainingPathDto) => {
    trainingPath.archived = !trainingPath.archived;
    httpClient.current?.put(`/trainingPath`, trainingPath).then(() => {
      if (pageNumbers > 1 && traingPathList.length === 1) {
        setCurrentPage(currentPage - 1);
        getCuurentTrainingPathSortedList(currentPage - 1);
      } else {
        getCuurentTrainingPathSortedList(currentPage);
      }
    });
  };

  function getSortIconClassName(columnName: string) {
    if (sortColumn === columnName) {
      return "fa fa-" + sortIcon;
    } else {
      return "fa fa-sort";
    }
  }

  return (
    <Row>
      {hasRoleUser ? (
        <>
          {!done  ? (
            <SpinnerComponent />

          ) : traingPathList.length === 0 && props.tabId === "1" ? (
            <Container className={styles["message_container"]}>
              <p className={styles["empty_message"]}>{props.t("no_training")}</p>
            </Container>
          ) 
          : traingPathList.length === 0 ? (
            <Container className={styles["message_container"]}>
              <p className={styles["empty_message"]}>{props.t("no_training_cycle")}</p>
            </Container>
          ) : (
            <>
              <Col sm="12">
                <div className={styles["card-training-container"]}>
                  {traingPathList.map(trainingPath => (
                    <TrainingPath
                      archiveTrainingPath={archiveTrainingPath}
                      key={trainingPath?.id}
                      displayMode={DisplayMode.GALLERY}
                      deleteTrainingPath={deleteTrainingPath}
                      changeStatus={activateTrainingPath}
                      cloneTrainingPath={refreshTrainaingPathList}
                      trainingPath={trainingPath}
                      tabId={props.tabId}
                    />
                  ))}
                </div>
              </Col>
              <Col sm="12">
                <PaginationComponent pageNumbers={pageNumbers} paginate={paginate} currentPage={currentPage}></PaginationComponent>
              </Col>
            </>
          )}
        </>
      ) : (
        <Col className={styles["cover-wrapper"]}>
          {hasRoleAdmin && <Breadcrumb parent={isArchivedList ? props.t("archived_training_path_list") : props.t("training_path_list")} />}
          <Container fluid>
            <Row>
              <Col sm="12">
                <Card>
                  {hasRoleAdmin && (
                    <CardHeader className={styles["training-card-header"]}>
                      {isArchivedList ? (
                        <h5>{props.t("archived_training_path_list")}</h5>
                      ) : (
                        <div className={styles["icons-display-training"]}>
                          <i
                            id={styles["display_mode_icon"]}
                            title="Vue liste"
                            className={displayMode === DisplayMode.LIST ? "fa fa-list text-success" : "fa fa-list"}
                            onClick={() => setDisplayMode(DisplayMode.LIST)}
                          ></i>
                          <i
                            id={styles["display_mode_icon"]}
                            title="Vue galerie"
                            className={displayMode === DisplayMode.GALLERY ? "fa fa-th-large text-success" : "fa fa-th-large"}
                            onClick={() => setDisplayMode(DisplayMode.GALLERY)}
                          ></i>
                          <div className="float-right">
                            <Button outline color="success" onClick={() => history.push("training-paths/add")}>
                              {props.t("create_cycle")}
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardHeader>
                  )}
                  {!done ? (
                    <SpinnerComponent />
                  ) : offline ? (
                    <Container className={styles["message_container"]}>
                      <SpinnerComponent />
                      <p className={styles["empty_message"]}>{props.t("offline_version_creation")}</p>
                    </Container>
                  ) : traingPathList.length === 0 ? (
                    <Container className={styles["message_container"]}>
                      <p className={styles["empty_message"]}>{props.t("no_training_cycle")}</p>
                    </Container>
                  ) : (
                    <Row>
                      <Col sm="12" lg="12" xl="12">
                        {displayMode === DisplayMode.LIST ? (
                          <div className={styles["card-block"]}>
                            <Table className="table-styling">
                              <thead>
                                <tr>
                                  <th className={styles["training-path-table-title"]} scope="col">
                                    <i className={getSortIconClassName("TRAINING_PATH_TITLE")} onClick={() => sortTrainingPathList("TRAINING_PATH_TITLE")}></i>
                                    {props.t("cycle_title")}
                                  </th>
                                  <th className={styles["training-path-table-modules"]} scope="col">
                                    <i className={getSortIconClassName("MODULES_NUMBER")} onClick={() => sortTrainingPathList("MODULES_NUMBER")}></i>
                                    {props.t("number_of_modules")}
                                  </th>
                                  <th className={styles["training-path-table-duration"]} scope="col">
                                    <i className={getSortIconClassName("TRAINING_PATH_TIME")} onClick={() => sortTrainingPathList("TRAINING_PATH_TIME")}></i>
                                    {props.t("average_duration")}
                                  </th>
                                  {!isArchivedList && (
                                    <th className={styles["training-path-table-status"]} scope="col">
                                      {props.t("status")}
                                    </th>
                                  )}
                                  <th className={styles["training-path-table-actions"]} scope="col">
                                    Actions
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {traingPathList.map(trainingPath => (
                                  <TrainingPath
                                    key={trainingPath?.id}
                                    displayMode={DisplayMode.LIST}
                                    deleteTrainingPath={deleteTrainingPath}
                                    changeStatus={activateTrainingPath}
                                    archiveTrainingPath={archiveTrainingPath}
                                    trainingPath={trainingPath}
                                    cloneTrainingPath={refreshTrainaingPathList}
                                    archived={isArchivedList}
                                  />
                                ))}
                              </tbody>
                            </Table>
                          </div>
                        ) : (
                          <div className={styles["card-training-container"]}>
                            {traingPathList.map(trainingPath => (
                              <TrainingPath
                                key={trainingPath?.id}
                                displayMode={DisplayMode.GALLERY}
                                deleteTrainingPath={deleteTrainingPath}
                                changeStatus={activateTrainingPath}
                                archiveTrainingPath={archiveTrainingPath}
                                cloneTrainingPath={refreshTrainaingPathList}
                                trainingPath={trainingPath}
                              />
                            ))}
                          </div>
                        )}
                        <PaginationComponent pageNumbers={pageNumbers} paginate={paginate} currentPage={currentPage} />
                      </Col>
                    </Row>
                  )}
                </Card>
              </Col>
            </Row>
          </Container>
        </Col>
      )}
    </Row>
  );
};

export default translate(TraingPathList);
